import { useEffect, useState } from 'react';
import MediaQuery from 'react-responsive';

export const breakpoints = {
  mobile: { max: 480 },
  phablet: { min: 481, max: 767 },
  tablet: { min: 768, max: 1023 },
  laptop: { min: 1024, max: 1279 },
  desktop: { min: 1280, max: 1439 },
  wide: { min: 1440 }
};

const DEVICE_TYPE_DICTIONARY = {
  smartphone: 'mobile',
  tablet: 'tablet',
  desktop: 'desktop'
};

// PRIVATE UTILS
const DEFAULT_DEVICE = 'mobile';

const isDeviceBiggerThan = (currentDeviceName, deviceName) =>
  Object.keys(breakpoints).indexOf(currentDeviceName) >
  Object.keys(breakpoints).indexOf(deviceName);

const getMediaQueryString = ({ min = 0, max = 0 }) =>
  `(min-width: ${min}px)${max ? ` and (max-width: ${max}px)` : ''}`;

const getDeviceFromMediaQueryString = mediaQuery => {
  const match = Object.entries(breakpoints)
    .map(([device, breakpoint]) => ({
      device,
      mediaQueryString: getMediaQueryString(breakpoint)
    }))
    .find(({ mediaQueryString }) => mediaQuery.includes(mediaQueryString));

  return match?.device || 'desktop';
};

const getInitialDevice = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return DEFAULT_DEVICE;
  }

  const mediaQueryLists = Object.values(breakpoints);
  const matchingQuery = mediaQueryLists.find(
    breakpoint => window.matchMedia(getMediaQueryString(breakpoint)).matches
  );

  if (matchingQuery) {
    return getDeviceFromMediaQueryString(getMediaQueryString(matchingQuery));
  }

  return DEFAULT_DEVICE;
};

const matchScreen = ({ min = 0, max = 0 }) =>
  typeof window !== 'undefined' &&
  window.matchMedia(getMediaQueryString({ min, max })).matches;

const withMediaQuery =
  ({ min = 0, max = 0 }) =>
  props =>
    (
      <MediaQuery
        {...(min ? { minWidth: min } : {})}
        {...(max ? { maxWidth: max } : {})}
        {...props}
      />
    );

const isBiggerThan = ({ max }) =>
  typeof window !== 'undefined' &&
  window.matchMedia(`(min-width: ${max + 1}px)`).matches;

const isSmallerThan = ({ min }) =>
  typeof window !== 'undefined' &&
  window.matchMedia(`(max-width: ${min}px)`).matches;

const getBreakpoint = ({ max }) =>
  typeof window !== 'undefined' && window.matchMedia(`(min-width: ${max}px)`);

// BOOLEAN FUNCTIONS
export const isMobileScreen = () => matchScreen(breakpoints.mobile);
export const isPhabletScreen = () => matchScreen(breakpoints.phablet);
export const isTabletScreen = () => matchScreen(breakpoints.tablet);
export const isLaptopScreen = () => matchScreen(breakpoints.laptop);
export const isDesktopScreen = () => matchScreen(breakpoints.desktop);
export const isWideScreen = () => matchScreen(breakpoints.wide);

export const isBiggerThanMobile = () => isBiggerThan(breakpoints.mobile);
export const isBiggerThanPhablet = () => isBiggerThan(breakpoints.phablet);
export const isBiggerThanTablet = () => isBiggerThan(breakpoints.tablet);
export const isBiggerThanLaptop = () => isBiggerThan(breakpoints.laptop);
export const isBiggerThanDesktop = () => isBiggerThan(breakpoints.desktop);
export const isBiggerThanWide = () => isBiggerThan(breakpoints.wide);

export const isSmallerThanPhablet = () => isSmallerThan(breakpoints.phablet);
export const isSmallerThanTablet = () => isSmallerThan(breakpoints.tablet);
export const isSmallerThanLaptop = () => isSmallerThan(breakpoints.laptop);
export const isSmallerThanDesktop = () => isSmallerThan(breakpoints.desktop);
export const isSmallerThanWide = () => isSmallerThan(breakpoints.wide);

// FROM MQ COMPONENTS
export const FromTablet = withMediaQuery({ min: breakpoints.tablet.min });
export const FromPhablet = withMediaQuery({ min: breakpoints.phablet.min });
export const FromLaptop = withMediaQuery({ min: breakpoints.laptop.min });
export const FromDesktop = withMediaQuery({ min: breakpoints.desktop.min });
export const FromWide = withMediaQuery({ min: breakpoints.wide.min });

// UNTIL MQ COMPONENTS
export const UntilMobile = withMediaQuery({ max: breakpoints.mobile.max });
export const UntilTablet = withMediaQuery({ max: breakpoints.tablet.max });
export const UntilPhablet = withMediaQuery({ max: breakpoints.phablet.max });
export const UntilLaptop = withMediaQuery({ max: breakpoints.laptop.max });
export const UntilDesktop = withMediaQuery({ max: breakpoints.desktop.max });

// ONLY MQ COMPONENTS
export const OnlyMobile = withMediaQuery(breakpoints.mobile);
export const OnlyPhablet = withMediaQuery(breakpoints.phablet);
export const OnlyTablet = withMediaQuery(breakpoints.tablet);
export const OnlyLaptop = withMediaQuery(breakpoints.laptop);
export const OnlyDesktop = withMediaQuery(breakpoints.desktop);
export const OnlyWide = withMediaQuery(breakpoints.wide);

export const getBreakpointTablet = () =>
  getBreakpoint({ max: breakpoints.tablet.max });

export const getFromTabletBreakpoint = () =>
  getBreakpoint({ max: breakpoints.laptop.min });

export function useMediaQuery(initialDevice) {
  // IMPORTANT: the lazy initializer must return the SAME value on SSR and on
  // the first client render. Previously this called `getInitialDevice()`,
  // which returns DEFAULT_DEVICE ('mobile') on the server (no `window`) but
  // reads `window.matchMedia` on the client and returns the real viewport.
  // The mismatch is a React hydration error (#418/#423/#425 in production
  // minified output) — observed on /s/<city> and /how-it-works. Seeding
  // DEFAULT_DEVICE here matches the SSR branch; the `initialMatch` block
  // inside useEffect below already promotes `device` to the real viewport
  // on the first client effect, so post-hydration behaviour is unchanged.
  // Backported from migration branch commit `edab2be6` (PLA-520).
  const [device, setDevice] = useState(
    DEVICE_TYPE_DICTIONARY[initialDevice] || DEFAULT_DEVICE
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return () => {};
    }

    const mediaQueryLists = Object.entries(breakpoints).map(
      ([deviceName, breakpoint]) => {
        const handleChange = event => {
          if (event.matches) {
            setDevice(getDeviceFromMediaQueryString(event.media));
          }
        };
        const mediaQuery = window.matchMedia(getMediaQueryString(breakpoint));
        mediaQuery.addEventListener('change', handleChange);

        return {
          deviceName,
          mediaQuery,
          handleChange
        };
      }
    );

    const initialMatch = mediaQueryLists.find(
      ({ mediaQuery }) => mediaQuery.matches
    );

    if (initialMatch && initialMatch.deviceName !== device) {
      setDevice(initialMatch.deviceName);
    }

    return () => {
      mediaQueryLists.forEach(({ mediaQuery, handleChange }) => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange);
        }
      });
    };
  }, []);

  return {
    device: device || DEFAULT_DEVICE,
    isMobileScreen: () => device === 'mobile',
    isPhabletScreen: () => device === 'phablet',
    isTabletScreen: () => device === 'tablet',
    isLaptopScreen: () => device === 'laptop',
    isDesktopScreen: () => device === 'desktop',
    isWideScreen: () => device === 'wide',
    isBiggerThanMobile: () => isDeviceBiggerThan(device, 'mobile'),
    isBiggerThanPhablet: () => isDeviceBiggerThan(device, 'phablet'),
    isBiggerThanTablet: () => isDeviceBiggerThan(device, 'tablet'),
    isBiggerThanLaptop: () => isDeviceBiggerThan(device, 'laptop'),
    isBiggerThanDesktop: () => isDeviceBiggerThan(device, 'desktop'),
    isBiggerThanWide: () => isDeviceBiggerThan(device, 'wide')
  };
}

export default breakpoints;
